/**
 * KYC HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getKYCVerification()       - GET /kyc-verification/sumsub-data - Get KYC verification data with Sumsub
 * • getKYCVerifications()      - GET /kyc-verification - Get all user KYC verifications
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useKYCVerification(kycLevel, enabled?)     - Query: Get KYC verification data
 * • useKYCVerifications(enabled?)              - Query: Get all user KYC verifications
 * • useInitiateKYCVerification()               - Mutation: Manually initiate KYC verification
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token (store + cookie fallback)
 * • Sumsub integration for KYC verification process
 * • Support for different KYC levels (e.g., 'basic-kyc-level')
 * • Returns Sumsub token and iframe URL for embedded verification
 * • 2-minute stale time (shorter than other data due to changing verification status)
 * • Automatic query invalidation on manual verification initiation
 * • Comprehensive error handling for KYC API responses
 * • Designed for compliance with financial regulations
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { getCurrentKycVerification, getDemoOverageRemaining } from '@/actions/kyc';
import fetchAPI from '@/lib/fetch-api';
import type { CurrentKYCVerification, DemoOverageRemaining } from '@/types/kyc';

// ==================== INTERFACES ====================

interface KYCVerificationData {
  status: string;
  sumsub_token: string;
  iframe_url: string;
}

interface UserInfo {
  id: number;
  name: string;
  avatar: string;
  email: string;
  joined: string;
}

interface SumsubPersonalDetails {
  tin: string;
  country: string;
  last_name: string;
  first_name: string;
  last_name_en: string;
  first_name_en: string;
  dob: string;
  gender: string;
  country_of_birth: string;
  phone: string;
  address: {
    street: string;
    city?: string;
    post_code: string;
    region?: string;
    state?: string;
    state_en?: string;
  };
}

interface SumsubSelfieDetails {
  image_id: number;
  inspection_id: string;
}

interface SumsubReviewDetails {
  attempt_cnt: number;
  create_date: string;
  review_date: string;
  reject_labels: string[];
  review_reject_type: string;
  client_comment: string;
  moderation_comment: string;
  review_status: string;
}

interface QuestionnaireItem {
  value: string;
  values: string[];
}

interface QuestionnaireSection {
  items: Record<string, QuestionnaireItem>;
}

interface Questionnaire {
  id: string;
  sections: Record<string, QuestionnaireSection>;
}

interface SumsubData {
  status: string;
  level: string;
  selfie_details: SumsubSelfieDetails;
  personal_details: SumsubPersonalDetails;
  review_details: SumsubReviewDetails;
  questionnaires: Questionnaire[];
}

interface KYCVerification {
  id: number;
  user: UserInfo;
  type: string;
  status: string;
  sumsub: SumsubData;
  requested: string;
  request_updated: string;
  reject_reason: string;
  additional_rejects: string[];
}

// KYC level permission that grants EPS card ordering eligibility
export const EPS_KYC_PERMISSION = 'eps';

/**
 * Checks whether the current KYC verification grants EPS card ordering permission
 * @param {CurrentKYCVerification | null | undefined} current - The current KYC verification data
 * @returns {boolean} True if the user has the EPS card permission
 */
export function hasEpsCardPermission(current?: CurrentKYCVerification | null): boolean {
  return !!current?.permissions?.includes(EPS_KYC_PERMISSION);
}

// ==================== API FUNCTIONS ====================

/**
 * Initiates or checks KYC verification with Sumsub
 * @param {string} kycLevel - The KYC level to be verified (e.g., 'ba sic-kyc-level')
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<KYCVerificationData>} The KYC verification data
 */
const getKYCVerification = async (kycLevel: string): Promise<KYCVerificationData> => {
  if (!kycLevel) {
    throw new Error('KYC level is required');
  }

  const response = await fetchAPI<KYCVerificationData>('/kyc-verification/sumsub-data', {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to get KYC verification data');
  }

  return response.data!;
};

/**
 * Fetches all KYC verifications for users
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<KYCVerification[]>} The KYC verifications array
 */
const getKYCVerifications = async (): Promise<KYCVerification[]> => {
  const response = await fetchAPI<KYCVerification[]>('/kyc-verification', {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to get KYC verifications');
  }

  return response.data!;
};

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to fetch KYC verification data for a specific KYC level
 * @param {string} kycLevel - The KYC level to fetch verification for
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for KYC verification
 */
export function useKYCVerification(
  kycLevel: string,
  enabled: boolean = true
): UseQueryResult<KYCVerificationData, Error> {
  return useQuery({
    queryKey: ['kyc-verification', kycLevel],
    queryFn: () => getKYCVerification(kycLevel),
    enabled: enabled && !!kycLevel,
    staleTime: 120000, // 2 minutes (shorter than wallet data since verification status can change)
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to initiate KYC verification (as a mutation for manual triggering)
 * @returns {UseMutationResult} Mutation result for initiating KYC verification
 */
export function useInitiateKYCVerification(): UseMutationResult<
  KYCVerificationData,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: getKYCVerification,
    onSuccess: (data, kycLevel) => {
      // Update the query cache with the new data
      queryClient.setQueryData(['kyc-verification', kycLevel], data);

      // Invalidate KYC-related queries
      queryClient.invalidateQueries({ queryKey: ['kyc-verification'] });
    },
  });
}

/**
 * Hook to fetch all KYC verifications for users
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for all KYC verifications
 */
export function useKYCVerifications(
  enabled: boolean = true
): UseQueryResult<KYCVerification[], Error> {
  return useQuery({
    queryKey: ['kyc-verifications'],
    queryFn: getKYCVerifications,
    enabled: enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

// ==================== DEMO OVERAGE ====================

/**
 * Hook to fetch the authenticated user's remaining demo overage allowance for a given currency.
 * Returns null in `remaining_amount` when the user is not demo-verified (no cap applies).
 * @param {number} currencyId - The currency in which the remaining allowance is returned
 * @param {boolean} enabled - Whether the query should be enabled
 */
export function useDemoOverageRemaining(
  currencyId?: number,
  enabled: boolean = true
): UseQueryResult<DemoOverageRemaining, Error> {
  return useQuery({
    queryKey: ['kyc-verification', 'demo-overage', currencyId],
    queryFn: () => getDemoOverageRemaining(currencyId!),
    enabled: enabled && !!currencyId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to fetch the authenticated user's current KYC verification with granted permissions
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for the current KYC verification
 */
export function useCurrentKYCVerification(
  enabled: boolean = true
): UseQueryResult<CurrentKYCVerification | null, Error> {
  return useQuery({
    queryKey: ['kyc-verification', 'current'],
    queryFn: getCurrentKycVerification,
    enabled: enabled,
    staleTime: 120000, // 2 minutes (verification status/permissions can change)
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}
